import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, Linking } from 'react-native';
import { useLocalSearchParams } from 'expo-router';

import { useTheme } from '../../src/context/ThemeContext';
import type { ColorTheme } from '../../src/utils/theme';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import ContentContainer from '../../src/components/ContentContainer';
import { api } from '../../src/api/client';

interface VendorBay {
  id: string;
  name: string;
  type: string;
  pricePerMinute: number | null;
  tiers: Array<{ name: string; price: number }> | null;
}

interface VendorLocation {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string | null;
  supportEmail: string | null;
  hours: Record<string, { open: string; close: string; closed?: boolean }> | null;
  bays: VendorBay[];
}

interface VendorData {
  accountNumber: string;
  businessName: string;
  acceptingPayments: boolean;
  locations: VendorLocation[];
  policies: { refund: string; return: string; cancellation: string };
}

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export default function VendorPublicScreen() {
  const { accountNumber } = useLocalSearchParams<{ accountNumber: string }>();
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [data, setData] = useState<VendorData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountNumber) return;
    const ac = new AbortController();
    setLoading(true);
    api<VendorData>(`/api/vendor/${encodeURIComponent(accountNumber)}`, { skipAuth: true })
      .then(d => { if (!ac.signal.aborted) setData(d); })
      .catch(err => { if (!ac.signal.aborted) setError(err?.message || 'Vendor not found'); })
      .finally(() => { if (!ac.signal.aborted) setLoading(false); });
    return () => ac.abort();
  }, [accountNumber]);

  if (loading) return <View style={styles.container}><ActivityIndicator size="large" color={colors.primary} style={{ marginTop: 60 }} /></View>;
  if (error || !data) return (
    <View style={styles.container}>
      <ContentContainer maxWidth={720}>
        <Text style={styles.title}>Vendor not found</Text>
        <Text style={styles.sectionContent}>{error || 'This vendor page is not available.'}</Text>
      </ContentContainer>
    </View>
  );

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <ContentContainer maxWidth={720}>
        <Text style={styles.title}>{data.businessName}</Text>
        {!data.acceptingPayments && (
          <View style={styles.banner}>
            <Text style={styles.bannerText}>This business is not yet accepting online payments.</Text>
          </View>
        )}

        {/* Services */}
        <Text style={styles.h2}>Services</Text>
        {data.locations.length === 0 && (
          <Text style={styles.sectionContent}>Service details coming soon.</Text>
        )}
        {data.locations.map(loc => (
          <View key={`svc-${loc.id}`} style={styles.section}>
            <Text style={styles.sectionTitle}>{loc.name}</Text>
            {loc.bays.length === 0 && <Text style={styles.sectionContent}>No bays listed.</Text>}
            {loc.bays.map(b => (
              <View key={b.id} style={styles.bayRow}>
                <Text style={styles.bayName}>{b.name}</Text>
                <Text style={styles.bayMeta}>
                  {b.type === 'automatic' ? 'Automatic' : 'Self-serve'}
                  {b.type === 'self-serve' && b.pricePerMinute != null
                    ? ` · $${b.pricePerMinute.toFixed(2)}/min`
                    : ''}
                </Text>
                {b.type === 'automatic' && b.tiers && b.tiers.length > 0 && (
                  <View style={{ marginTop: spacing.xs }}>
                    {b.tiers.map((t, i) => (
                      <Text key={i} style={styles.bayMeta}>
                        {t.name} — ${(t.price / 100).toFixed(2)}
                      </Text>
                    ))}
                  </View>
                )}
              </View>
            ))}
          </View>
        ))}

        {/* Customer service */}
        <Text style={styles.h2}>Customer Service</Text>
        {data.locations.map(loc => (
          <View key={`cs-${loc.id}`} style={styles.section}>
            <Text style={styles.sectionTitle}>{loc.name}</Text>
            <Text style={styles.sectionContent}>{loc.address}, {loc.city}, {loc.state} {loc.zip}</Text>
            {loc.phone && (
              <Text style={styles.link} onPress={() => Linking.openURL(`tel:${loc.phone}`)}>
                Phone: {loc.phone}
              </Text>
            )}
            {loc.supportEmail && (
              <Text style={styles.link} onPress={() => Linking.openURL(`mailto:${loc.supportEmail}`)}>
                Email: {loc.supportEmail}
              </Text>
            )}
            {loc.hours && (
              <View style={{ marginTop: spacing.sm }}>
                <Text style={styles.sectionSubtitle}>Hours</Text>
                {DAY_ORDER.map(day => {
                  const h = loc.hours?.[day];
                  if (!h) return null;
                  return (
                    <Text key={day} style={styles.sectionContent}>
                      {cap(day)}: {h.closed ? 'Closed' : `${h.open} – ${h.close}`}
                    </Text>
                  );
                })}
              </View>
            )}
          </View>
        ))}

        {/* Policies */}
        <Text style={styles.h2}>Policies</Text>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Refund Policy</Text>
          <Text style={styles.sectionContent}>{data.policies.refund}</Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Return Policy</Text>
          <Text style={styles.sectionContent}>{data.policies.return}</Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Cancellation Policy</Text>
          <Text style={styles.sectionContent}>{data.policies.cancellation}</Text>
        </View>

        <Text style={styles.footer}>Account: {data.accountNumber}</Text>
        <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

function cap(s: string) { return s.charAt(0).toUpperCase() + s.slice(1); }

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.lg },
  title: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  h2: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  section: { marginBottom: spacing.md, backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md, borderWidth: 1, borderColor: c.borderLight },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.sm },
  sectionSubtitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.xs },
  sectionContent: { fontSize: fontSize.sm, color: c.textSecondary, lineHeight: 22, marginBottom: spacing.xs },
  bayRow: { paddingVertical: spacing.sm, borderTopWidth: 1, borderTopColor: c.borderLight },
  bayName: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text },
  bayMeta: { fontSize: fontSize.xs, color: c.textSecondary },
  link: { fontSize: fontSize.sm, color: c.primary, marginTop: spacing.xs },
  banner: { backgroundColor: c.surface, borderWidth: 1, borderColor: c.borderLight, borderRadius: borderRadius.sm, padding: spacing.sm, marginBottom: spacing.md },
  bannerText: { fontSize: fontSize.xs, color: c.textSecondary },
  footer: { fontSize: fontSize.xs, color: c.textLight, textAlign: 'center', marginTop: spacing.lg },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
